import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingCart, AlertTriangle, Lock, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BuyerApprovalBanner from "@/components/BuyerApprovalBanner";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR, tierRate } from "@/lib/format";
import ProductImg from "@/components/ProductImg";

export default function CartPage() {
  const { items, updateQty, removeItem } = useCart();
  const { user, approved, config } = useAuth();
  const navigate = useNavigate();

  const taxRate = config.tax_rate ?? 0.18;
  const lineTotal = (i) => tierRate(i, i.quantity) * i.quantity;
  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  const minOrder = config.min_order_value ?? 6000;
  const belowMin = subtotal < minOrder;
  const moqIssues = items.filter((i) => i.quantity < (i.moq || 1) || ((i.pack_size || 1) > 1 && i.quantity % i.pack_size !== 0));
  const canCheckout = approved && items.length > 0 && moqIssues.length === 0 && !belowMin;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <BuyerApprovalBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">Your cart</h1>

        {items.length === 0 ? (
          <div className="text-center py-24" data-testid="empty-cart">
            <ShoppingCart className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-6">Your cart is empty.</p>
            <Button asChild className="rounded-full bg-slate-900 hover:bg-black text-white"><Link to="/">Browse catalogue</Link></Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 [&>*]:min-w-0">
            <div className="lg:col-span-2 space-y-4 min-w-0">
              {items.map((i) => {
                const rate = approved ? tierRate(i, i.quantity) : null;
                const belowMoq = i.quantity < (i.moq || 1);
                const packBad = (i.pack_size || 1) > 1 && i.quantity % i.pack_size !== 0;
                return (
                  <div key={i.product_id} data-testid={`cart-item-${i.sku}`} className="flex gap-3 sm:gap-4 bg-white rounded-2xl border border-slate-200 p-3 sm:p-4 min-w-0">
                    <ProductImg src={i.image_url} alt={i.name} className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[11px] font-mono-num text-slate-400">SKU {i.sku}</div>
                          <h3 className="font-heading font-semibold text-slate-900 line-clamp-2 break-words">{i.name}</h3>
                          <div className="text-xs text-slate-500 mt-0.5">MOQ {i.moq} {/^(box|boxes|pack|packs|set|sets)$/i.test(i.unit || "") ? (i.moq === 1 ? "pack" : "packs") : "pcs"}{i.pack_size > 1 ? ` · packs of ${i.pack_size}` : ""}</div>
                        </div>
                        <button onClick={() => removeItem(i.product_id)} className="text-slate-400 hover:text-rose-600 h-fit" data-testid={`remove-${i.sku}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center rounded-full border border-slate-200">
                          <button className="p-2 text-slate-500 hover:text-slate-900" onClick={() => updateQty(i.product_id, Math.max(i.pack_size || 1, i.quantity - (i.pack_size || 1)))} data-testid={`cart-dec-${i.sku}`}><Minus className="h-3.5 w-3.5" /></button>
                          <span className="w-10 text-center text-sm font-semibold font-mono-num">{i.quantity}</span>
                          <button className="p-2 text-slate-500 hover:text-slate-900" onClick={() => updateQty(i.product_id, i.quantity + (i.pack_size || 1))} data-testid={`cart-inc-${i.sku}`}><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        {approved && <div className="font-mono-num font-bold text-slate-900">{formatINR(rate * i.quantity)}</div>}
                      </div>
                      {(belowMoq || packBad) && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid={`moq-warning-${i.sku}`}>
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {belowMoq ? `Below minimum order quantity (${i.moq})` : `Must be in multiples of ${i.pack_size}`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24">
                <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Order summary</h2>
                {!approved ? (
                  <div className="text-center py-4">
                    <Lock className="h-7 w-7 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500 mb-4">{user ? "Pricing unlocks once your account is approved." : "Sign in with an approved account to see totals and checkout."}</p>
                    <Button onClick={() => navigate(user ? "/account" : "/login")} className="w-full rounded-full bg-slate-900 hover:bg-black text-white" data-testid="cart-login-btn">
                      {user ? "Account status" : "Sign in"}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-num font-medium" data-testid="cart-subtotal">{formatINR(subtotal)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">GST ({Math.round(taxRate * 100)}%)</span><span className="font-mono-num font-medium">{formatINR(tax)}</span></div>
                      <div className="border-t border-slate-100 pt-2.5 flex justify-between text-base"><span className="font-semibold">Total</span><span className="font-mono-num font-bold" data-testid="cart-total">{formatINR(total)}</span></div>
                    </div>
                    {belowMin && (
                      <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2.5" data-testid="min-order-warning">
                        <div className="flex items-center justify-between text-xs font-semibold text-amber-900">
                          <span data-testid="min-order-remaining">{formatINR(Math.round((minOrder - subtotal) * 100) / 100)} more to reach {formatINR(minOrder)}</span>
                          <span className="font-mono-num">{Math.floor((subtotal / minOrder) * 100)}%</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-amber-100 overflow-hidden" role="progressbar" aria-valuenow={Math.floor((subtotal / minOrder) * 100)} aria-valuemin={0} aria-valuemax={100} data-testid="min-order-progress">
                          <div className="h-full rounded-full bg-[#FFDA00] transition-[width] duration-500" style={{ width: `${Math.min(100, (subtotal / minOrder) * 100)}%` }} />
                        </div>
                        <p className="text-[11px] text-amber-800 mt-1.5">Minimum order value is {formatINR(minOrder)} (before GST).</p>
                      </div>
                    )}
                    {!belowMin && items.length > 0 && (
                      <div className="mt-4 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="min-order-reached">Minimum order value reached — you're good to checkout.</div>
                    )}
                    {moqIssues.length > 0 && (
                      <div className="mt-4 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" /> Resolve MOQ issues to checkout.
                      </div>
                    )}
                    <Button disabled={!canCheckout} onClick={() => navigate("/checkout")} className="w-full mt-5 h-12 rounded-full bg-[#FFDA00] hover:bg-[#f2ce00] text-slate-900 font-semibold disabled:opacity-50" data-testid="proceed-checkout-btn">
                      Proceed to checkout <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
